import { useState, useEffect } from "react";
import { Calendar, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";

interface BlogPost {
  id?: number;
  title: string;
  excerpt: string;
  date: string;
  link?: string;
  featured_image?: string;
  slug?: string;
}

const BlogPage = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { theme } = useTheme();

  // Replace 'your-site-name' with your actual WordPress.com site name
  const WORDPRESS_SITE = "docgendev.wordpress.com";
  
  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      // WordPress.com REST API endpoint - requesting URL field specifically
      const response = await fetch(
        `https://public-api.wordpress.com/rest/v1.1/sites/${WORDPRESS_SITE}/posts?number=10&fields=id,title,excerpt,date,URL,slug,featured_image`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch blog posts');
      }
      
      const data = await response.json();
      console.log('API Response:', data); // Debug log
      
      // WordPress.com API returns posts directly in the response
      if (data.posts && Array.isArray(data.posts)) {
        // Debug: Log each post to see available fields
        data.posts.forEach((post: any, index: number) => {
          console.log(`Post ${index + 1} fields:`, Object.keys(post));
          console.log(`Post ${index + 1} data:`, post);
        });
        
        // Map the WordPress.com API format to our expected format
        const mappedPosts = data.posts.map((post: any, index: number) => {
          // Try multiple ways to get the post URL
          let postUrl = post.URL || post.link;
          
          // If no URL provided, construct it manually
          if (!postUrl || postUrl === '#') {
            if (post.slug) {
              postUrl = `https://${WORDPRESS_SITE}/${post.slug}/`;
            } else if (post.id) {
              postUrl = `https://${WORDPRESS_SITE}/?p=${post.id}`;
            } else {
              postUrl = `https://${WORDPRESS_SITE}/`;
            }
          }
          
          console.log(`Mapped post ${index + 1} URL:`, postUrl);
          
          return {
            id: post.id || index + 1,
            title: post.title || 'Untitled',
            excerpt: post.excerpt || '',
            date: post.date || new Date().toISOString(),
            link: postUrl,
            featured_image: post.featured_image || '',
            slug: post.slug || ''
          };
        });
        
        console.log('Final mapped posts:', mappedPosts);
        setPosts(mappedPosts);
      } else {
        // No posts found
        setPosts([]);
      }
      
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load blog posts');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const handlePostClick = (link: string) => {
    console.log('Clicking post with link:', link);
    if (link && link !== "#" && link !== '') {
      window.open(link, '_blank');
    } else {
      console.warn('No valid link available for this post');
      // Optional: Show a toast or alert to user
      alert('Sorry, this blog post link is not available yet.');
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen pt-20 ${theme === 'dark' ? 'bg-[#030405]' : 'bg-background'}`}>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center mb-12">
            <div className={`h-8 w-64 mx-auto mb-4 rounded ${theme === 'dark' ? 'bg-card' : 'bg-gray-200'} animate-pulse`}></div>
            <div className={`h-4 w-96 mx-auto rounded ${theme === 'dark' ? 'bg-card' : 'bg-gray-200'} animate-pulse`}></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className={`p-6 rounded-lg bg-card shadow-sm`}>
                <div className={`h-4 w-3/4 mb-4 rounded ${theme === 'dark' ? 'bg-muted' : 'bg-gray-200'} animate-pulse`}></div>
                <div className={`h-3 w-full mb-2 rounded ${theme === 'dark' ? 'bg-muted' : 'bg-gray-200'} animate-pulse`}></div>
                <div className={`h-3 w-2/3 rounded ${theme === 'dark' ? 'bg-muted' : 'bg-gray-200'} animate-pulse`}></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pt-20 ${theme === 'dark' ? 'bg-[#030405]' : 'bg-background'}`}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className={`text-4xl font-bold mb-4 ${theme === 'dark' 
            ? 'text-transparent bg-clip-text bg-gradient-to-br from-white to-gray-400' 
            : 'text-transparent bg-clip-text bg-gradient-to-br from-gray-800 to-gray-600'}`}>
            Our Blog
          </h1>
          <p className={`text-lg max-w-2xl mx-auto mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Insights, tutorials, and updates from the DocGen team
          </p>
          
          {/* View Full Blog Button */}
          <Button
            variant="outline"
            className={`${theme === 'dark' 
              ? 'border-primary/60 text-primary hover:text-primary hover:bg-primary/10' 
              : 'border-emerald-600 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50'} 
              transition-all duration-300 transform hover:scale-105`}
            onClick={() => window.open('https://docgendev.wordpress.com', '_blank')}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            View Full Blog & Comments
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className={`p-4 rounded-lg mb-8 ${theme === 'dark' ? 'bg-destructive/20 border border-destructive/30' : 'bg-red-50 border border-red-200'}`}>
            <p className={`text-center ${theme === 'dark' ? 'text-destructive-foreground' : 'text-red-700'}`}>
              Failed to load blog posts. Please try again later.
            </p>
          </div>
        )}

        {/* Blog Posts Grid */}
        {posts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {posts.map((post) => (
              <article
                key={post.id}
                className={`p-6 rounded-lg shadow-sm border border-border transition-all duration-200 hover:shadow-md bg-card hover:border-border/70 ${
                  post.link && post.link !== '#' && post.link !== '' ? 'cursor-pointer' : 'cursor-default'
                }`}
                onClick={() => handlePostClick(post.link || '#')}
              >
                {/* Date */}
                <div className={`flex items-center text-sm mb-3 text-muted-foreground`}>
                  <Calendar className="h-4 w-4 mr-2" />
                  {formatDate(post.date)}
                </div>

                {/* Title */}
                <h2 className={`text-xl font-semibold mb-3 line-clamp-2 text-foreground ${theme === 'dark' 
                  ? 'hover:text-primary' 
                  : 'hover:text-emerald-700'} transition-colors`}>
                  {stripHtml(post.title)}
                </h2>

                {/* Excerpt */}
                <p className={`text-sm mb-4 line-clamp-3 text-muted-foreground`}>
                  {stripHtml(post.excerpt)}
                </p>

                {/* Read More Link */}
                <div className={`flex items-center text-sm font-medium ${theme === 'dark' 
                  ? 'text-primary hover:text-primary/80' 
                  : 'text-emerald-700 hover:text-emerald-800'} transition-colors`}>
                  Read more
                  <ExternalLink className="h-3 w-3 ml-1" />
                </div>
              </article>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-12">
            <div className={`p-8 rounded-lg max-w-md mx-auto bg-card`}>
              <div className="text-4xl mb-4">📝</div>
              <h3 className={`text-xl font-semibold mb-2 text-foreground`}>
                No blog posts yet
              </h3>
              <p className={`mb-6 text-muted-foreground`}>
                We're working on some amazing content. Check back soon!
              </p>
              <Button onClick={fetchBlogPosts}>
                Refresh
              </Button>
            </div>
          </div>
        )}

        {/* Setup Instructions */}
      </div>
    </div>
  );
};

export default BlogPage; 